import { useEffect } from "react"

export function loadFont(fontFamily: string): void {
  const fontName = fontFamily.split(",")[0].trim()
  const fontParam = fontName.replace(/ /g, "+")
  const linkId = `google-font-${fontParam}`
  if (document.getElementById(linkId)) return

  const link = document.createElement("link")
  link.id = linkId
  link.href = `https://fonts.googleapis.com/css2?family=${fontParam}:wght@400;500;600;700&display=swap`
  link.rel = "stylesheet"
  document.head.appendChild(link)
}

export function useGoogleFont(fontFamily?: string) {
  useEffect(() => {
    if (!fontFamily) return
    loadFont(fontFamily)
  }, [fontFamily])
}

/** Preload multiple fonts (e.g. for a font selector dropdown) */
export function usePreloadFonts(fontFamilies: readonly { value: string }[]) {
  useEffect(() => {
    fontFamilies.forEach((f) => loadFont(f.value))
  }, [fontFamilies])
}

/**
 * CSS size scales injected into .nos-site-root for S/L font size selections.
 *
 * Medium injects nothing — blocks keep their responsive Tailwind classes
 * (hero h1 runs 2.25rem → 4.5rem across sm/md/lg breakpoints). Small and
 * Large therefore MUST be responsive too, and stay strictly below / above
 * medium's largest per-tag class at every breakpoint — a flat override
 * (e.g. h1 locked at 4rem) ends up SMALLER than medium's 4.5rem on desktop.
 */
const FONT_SIZE_CSS: Record<string, string> = {
  small: `
    .nos-site-root p,
    .nos-site-root li,
    .nos-site-root blockquote,
    .nos-site-root td,
    .nos-site-root th { font-size: 0.875rem; }
    .nos-site-root h1 { font-size: 2rem; }
    .nos-site-root h2 { font-size: 1.5rem; }
    .nos-site-root h3 { font-size: 1.25rem; }
    .nos-site-root h4 { font-size: 1.125rem; }
    @media (min-width: 640px) {
      .nos-site-root h1 { font-size: 2.5rem; }
    }
    @media (min-width: 768px) {
      .nos-site-root h1 { font-size: 3rem; }
      .nos-site-root h2 { font-size: 1.75rem; }
    }
    @media (min-width: 1024px) {
      .nos-site-root h1 { font-size: 3.5rem; }
    }
  `,
  medium: "",
  large: `
    .nos-site-root p,
    .nos-site-root li,
    .nos-site-root blockquote,
    .nos-site-root td,
    .nos-site-root th { font-size: 1.125rem; }
    .nos-site-root h1 { font-size: 2.75rem; }
    .nos-site-root h2 { font-size: 2.25rem; }
    .nos-site-root h3 { font-size: 1.75rem; }
    .nos-site-root h4 { font-size: 1.375rem; }
    @media (min-width: 640px) {
      .nos-site-root p,
      .nos-site-root li,
      .nos-site-root blockquote { font-size: 1.25rem; }
      .nos-site-root h1 { font-size: 3.5rem; }
      .nos-site-root h2 { font-size: 2.75rem; }
    }
    @media (min-width: 768px) {
      .nos-site-root h1 { font-size: 4.25rem; }
      .nos-site-root h2 { font-size: 3.5rem; }
      .nos-site-root h3 { font-size: 2rem; }
    }
    @media (min-width: 1024px) {
      .nos-site-root h1 { font-size: 5rem; }
    }
  `,
}

/**
 * Loads heading + body fonts from Google Fonts and injects a scoped <style> into
 * <head> that applies heading font and optional font size overrides to all
 * .nos-site-root elements. Body font is applied via fontFamily inline style on the
 * .nos-site-root wrapper and propagates through CSS inheritance.
 */
export function useSiteStyles(headingFont: string, bodyFont: string, fontSize: string = "medium") {
  useEffect(() => {
    loadFont(headingFont)
    if (bodyFont !== headingFont) loadFont(bodyFont)

    const id = "nos-site-styles"
    let el = document.getElementById(id) as HTMLStyleElement | null
    if (!el) {
      el = document.createElement("style")
      el.id = id
      document.head.appendChild(el)
    }
    el.textContent = `
      .nos-site-root h1,
      .nos-site-root h2,
      .nos-site-root h3,
      .nos-site-root h4,
      .nos-site-root h5,
      .nos-site-root h6 {
        font-family: ${headingFont};
      }
      ${FONT_SIZE_CSS[fontSize] ?? ""}
    `
  }, [headingFont, bodyFont, fontSize])
}

/** Map font_size bucket to a CSS px value */
export const FONT_SIZE_MAP: Record<string, string> = {
  small: "14px",
  medium: "16px",
  large: "18px",
}
